import React, { startTransition, useEffect, useMemo, useState } from "react";
import CarteDropdown, {
  ICarteItem,
  ICarteSection,
} from "design-system/src/Components/CarteDropdown/CarteDropdown";
import { useTranslation } from "@src/hooks/useTranslation";
import { useRollingStartNavigation } from "@src/lib/rolling-start-navigation";
import { CarteMenuQuery } from "@src/../graphql-types";
import { ArrayElement } from "@src/interface/utils";
import useNavigationUrlFactory from "@src/hooks/useNavigationUrlFactory";
import useBackboneContext from "@src/backbone/provider/useBackboneContext";
import Portal from "design-system/src/Components/Portal/Portal";
import Link from "design-system/src/Core/Link/Link";
import { useAllCategory } from "@src/hooks/query/useAllCategory";
import useShowProductPrice from "@src/lib/LocationFirst/useShowProductPrice";
import useCartModelChange from "@src/backbone/hooks/useCartModelChange";

export type CarteMenuCategory = ArrayElement<
  CarteMenuQuery["allCategory"]["nodes"]
>;

const CarteMenu = (props: {
  className?: string;
  disableDropDown?: boolean;
}) => {
  const { className, disableDropDown } = props;
  const { t } = useTranslation(["Navigation"]);
  const routes = useNavigationUrlFactory();
  const { navigate } = useRollingStartNavigation();
  const [, cart] = useCartModelChange(["id_store"]);
  const { app } = useBackboneContext();
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const { data: allCategory } = useAllCategory();
  const [activeCategoryIds, setActiveCategoryIds] = useState<Set<string>>(
    () =>
      new Set(
        allCategory
          .filter(({ active_menu }) => active_menu)
          .map(({ id_category }) => id_category as string)
      )
  );
  const { hasLocation } = useShowProductPrice();

  useEffect(() => {
    const handler = () => {
      const backboneTotal = app?.getCategories()?.length ?? 0;
      // LCP-FIX: SetupModel est instancié en post-LCP — ne pas écraser le fallback
      // GraphQL tant que la collection Backbone « category » est vide.
      if (backboneTotal === 0) {
        return;
      }

      const categories = app.getCategories().getActiveMenuInContext();
      const ids = categories
        .map((category) => {
          // Check if all products in the category are out of stock
          const products = category.getProducts(true);
          if (!products || products.length === 0) return undefined;

          const allOutOfStock = products.every((product) =>
            product.isStockout()
          );
          return !allOutOfStock ? category.id.toString() : undefined;
        })
        .filter((id): id is string => id !== undefined);

      startTransition(() => {
        setActiveCategoryIds(new Set(ids));
      });
    };

    app.on("all", handler);
    app.on("setupInitialized", handler);
    app.on("setupReady", handler);
    handler();

    return () => {
      app.off("all", handler);
      app.off("setupReady", handler);
      app.off("setupInitialized", handler);
    };
  }, [app]);

  const carteCategories = allCategory.filter(({ active_menu }) => active_menu);

  // carte menu list
  const list: ICarteSection[] = useMemo(() => {
    const categories: CarteMenuCategory[] = [];
    const collaborationsCategory: CarteMenuCategory[] = [];
    const groupList = [];

    const mapCategories = (list: CarteMenuCategory[]) =>
      list.map<ICarteItem>((category) => {
        const { title_color, short_name, name, uri } = category;
        return {
          label: short_name || name || "",
          url: uri || "",
          title_color,
          onClick: (e) => {
            e.preventDefault();
            navigate(category.uri || "");
          },
        };
      });

    if (activeCategoryIds.size > 0) {
      carteCategories.forEach((node) => {
        if (
          node.id_category &&
          activeCategoryIds.has(node.id_category.toString())
        ) {
          if (node.collaboration) {
            // @ts-ignore
            collaborationsCategory.push(node);
          } else {
            // @ts-ignore
            categories.push(node);
          }
        }
      });

      if (collaborationsCategory.length) {
        groupList.push({
          label: t("Collaboration", "Navigation"),
          links: mapCategories(collaborationsCategory),
        });
      }
    }

    return [
      ...groupList,
      {
        label: t("Carte", "Navigation"),
        links: mapCategories(categories),
      },
    ];
  }, [carteCategories, t, activeCategoryIds, navigate]);

  const navigationUrl = routes.allCategories();

  const handlePopupVisibleChange = (visible: boolean) => {
    if (isPopupVisible !== visible) {
      setIsPopupVisible(visible);
    }
  };

  useEffect(() => {
    if (disableDropDown) {
      if (isPopupVisible) {
        startTransition(() => {
          setIsPopupVisible(false);
        });
      }
    }
  }, [disableDropDown, isPopupVisible]);

  const handleNavigateToListing = async () => {
    if (!hasLocation) {
      cart.requireDeliveryOptions({ redirect: "close", fullScreen: true });
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  };

  return disableDropDown ? (
    <Link
      className={className}
      color="white"
      url={navigationUrl}
      beforeClick={handleNavigateToListing}
      label={t("Carte", "Navigation")}
    />
  ) : (
    <>
      <CarteDropdown
        className={className}
        label={t("Carte", "Navigation")}
        list={list}
        url={navigationUrl}
        beforeClick={handleNavigateToListing}
        onVisibleChange={handlePopupVisibleChange}
      />
      <Portal hide={!isPopupVisible}>
        {isPopupVisible && (
          <div
            style={{
              position: "fixed",
              left: 0,
              top: 0,
              width: "100%",
              height: "100%",
              background: "rgba(0,0,0,0.5)",
              zIndex: 4,
            }}
          ></div>
        )}
      </Portal>
    </>
  );
};

export default CarteMenu;
